import { Checkbox, FlexBox, Text } from '@dixa/lib-shared-habla';
import Fuse from 'fuse.js';
import { useHasAccess } from 'lib-shared-access-control';
import {
  getAsyncStatus,
  getAsyncStatusFromRtkQueryStates,
} from 'lib-shared-auth';
import { ampli } from 'lib-shared-tracking';
import type { AsyncStatus, Tag } from 'lib-shared-types-api';
import { useCallback, useMemo, useState } from 'react';

import { useReadSearchInfiniteQuery } from 'apis/search';
import { tagsApi } from 'apis/tags';
import {
  CategorizedList,
  CategorizedListCategory,
  CategorizedListProps,
} from 'components/categorizedList';

import { TagCreateItemContent } from './components/tagCreateItemContent/tagCreateItemContent';
import { TagItemContent } from './components/tagItemContent/tagItemContent';

export type CategorizedTagsListProps = Pick<
  CategorizedListProps<Tag>,
  'onChange'
> & {
  /** The selected tags. */
  selectedTags: readonly Tag[];
  /** Whether the list is active. */
  isActive: boolean;
};

/** Represents the categorized tags list. */
export const CategorizedTagsList = ({
  selectedTags,
  isActive,
  onChange,
}: CategorizedTagsListProps): JSX.Element => {
  const hasManageTagsAccess = useHasAccess('settings.tag');

  const [includeDeactivated, setIncludeDeactivated] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>();

  const tagCategoriesResult = tagsApi.useReadTagCategoriesQuery();

  const tagSearchResult = useReadSearchInfiniteQuery({
    scope: 'tags',
    query: searchTerm,
    sortBy: { field: 'name', order: 'asc' },
    ...(!includeDeactivated && {
      filters: {
        strategy: 'all',
        filters: [
          {
            field: 'is_deactivated',
            operator: 'not_equals',
            values: [{ value: 'true' }],
          },
        ],
      },
    }),
  });

  const categories = useMemo<CategorizedListCategory<Tag>[]>(() => {
    const tagCategories = tagCategoriesResult.data?.data.categories ?? [];

    if (
      tagSearchResult.isFetching &&
      !tagSearchResult.results.length &&
      tagCategoriesResult.isFetching
    ) {
      return [{ id: 'paginated', items: [] }];
    }

    const fuse = new Fuse<Tag>([], { keys: ['name'], threshold: 0.4 });

    return [
      ...tagCategories
        .map(({ id, items }) => {
          let filtered = items as Tag[];

          if (searchTerm) {
            fuse.setCollection(filtered);
            filtered = fuse.search(searchTerm).map(({ item }) => item);
          }

          if (!includeDeactivated) {
            filtered = filtered.filter((tag) => !tag.is_deactivated);
          }

          return { id, items: filtered };
        })
        .filter((category) => category.items.length),
      { id: 'paginated', items: tagSearchResult.results as Tag[] },
    ];
  }, [
    tagCategoriesResult.data?.data.categories,
    tagCategoriesResult.isFetching,
    tagSearchResult.isFetching,
    tagSearchResult.results,
    searchTerm,
    includeDeactivated,
  ]);

  const asyncStatus = useMemo<AsyncStatus>(
    () =>
      getAsyncStatus([
        getAsyncStatusFromRtkQueryStates(tagSearchResult),
        getAsyncStatusFromRtkQueryStates(tagCategoriesResult),
      ]),
    [tagCategoriesResult, tagSearchResult],
  );

  const favorites = useMemo<ReadonlySet<string>>(
    () =>
      new Set(
        (tagCategoriesResult.data?.data.categories ?? [])
          .filter((category) => category.id === 'favorite')
          .flatMap((category) => category.items)
          .map((tag) => tag.id),
      ),
    [tagCategoriesResult.data?.data.categories],
  );

  const handleSearchChange = useCallback((term: string) => {
    setSearchTerm(term.trim() || undefined);
  }, []);

  const handleTagStateChange = useCallback(() => {
    setIncludeDeactivated((prev) => {
      const next = !prev;

      ampli.conversationHeaderTagIncludeDeactivatedToggled({
        include: next,
      });

      return next;
    });
  }, []);

  const refetch = useCallback(() => {
    tagCategoriesResult.refetch();
    tagSearchResult.refetch();
  }, [tagCategoriesResult, tagSearchResult]);

  const showCreate = useMemo(
    () =>
      hasManageTagsAccess &&
      asyncStatus === 'fulfilled' &&
      typeof searchTerm !== 'undefined' &&
      searchTerm.trim().length > 1 &&
      categories.every((category) =>
        category.items.every(
          (tag) =>
            tag.name.toLocaleLowerCase() !== searchTerm.trim().toLowerCase(),
        ),
      ),
    [searchTerm, hasManageTagsAccess, asyncStatus, categories],
  );

  return (
    <CategorizedList
      categories={categories}
      selectedItems={selectedTags}
      asyncStatus={asyncStatus}
      hasNextPage={tagSearchResult.hasNextPage}
      showSearch
      isMultiple
      resetSearchOnInactive
      isActive={isActive}
      isItemDisabled={(tag) =>
        tag.is_deactivated && !selectedTags.some(({ id }) => id === tag.id)
      }
      isItemFavorite={(tag) => favorites.has(tag.id)}
      identityFn={(tag) => tag.id}
      categoriesTopSlot={
        <FlexBox alignItems="center" gap="200" padding="200 300" as="label">
          <Checkbox
            name="active"
            checked={includeDeactivated}
            onChange={handleTagStateChange}
          />
          <Text.Paragraph variant="3">Include deactivated tags</Text.Paragraph>
        </FlexBox>
      }
      ItemContent={TagItemContent}
      CreateItemContent={showCreate ? TagCreateItemContent : undefined}
      nextPage={tagSearchResult.fetchNextPage}
      refetch={refetch}
      onSearchChange={handleSearchChange}
      onChange={onChange}
    />
  );
};
